/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import { formatMessage } from './intlUtils';
import messages from './messages';

const contextPath = process.env.PUBLIC_URL;

/**
 * The message keys used to map the keys of messages from the server.
 */
export const actionURLs = {
  changePassword: `${contextPath}selfmgt/changepassword`,
  createUser: `${contextPath}useraction/createuser`,
  samlLogin: `${contextPath}useraction/samllogin`,
  recoverPassword: `${contextPath}selfmgt/recoverpassword`,
  resetPassword: `${contextPath}selfmgt/resetpassword`,
};

const getText = (key, defaultValue) => {
  return formatMessage(key) || defaultValue;
};

const getTextWithValues = (key, values) => {
  return formatMessage(key, values);
};

const getTextOrDefault = (key, defaultKey, fieldName) => {
  return fieldName
    ? formatMessage(key, {
        fieldName: fieldName,
      })
    : formatMessage(defaultKey);
};

const getUsernameText = (usernameLabel) => {
  return usernameLabel || formatMessage(messages.usernameLabel);
};

const getPasswordText = (passwordLabel) => {
  return passwordLabel || formatMessage(messages.passwordLabel);
};

const getRequiredFieldText = (fieldName) => {
  return formatMessage(messages.fieldIsRequired, {
    fieldName: fieldName,
  });
};

const getLoginHelpText = (fieldName) => {
  return getTextOrDefault(
    messages.loginHelpText,
    messages.defaultLoginHelpText,
    fieldName,
  );
};

const getRequestPasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.requestField,
    messages.requestPassword,
    fieldName,
  );
};

const getChangePasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.changeField,
    messages.changePassword,
    fieldName,
  );
};

const getNewPasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.newFieldLabel,
    messages.newPassword,
    fieldName,
  );
};

const getConfirmPasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.confirmField,
    messages.confirmPassword,
    fieldName,
  );
};

const getCurrentPasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.currentFieldLabel,
    messages.currentPassword,
    fieldName,
  );
};
const getConfirmNewPasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.confirmNewFieldLabel,
    messages.confirmNewPassword,
    fieldName,
  );
};

const getPasswordRequirementsNotMetText = (fieldName) => {
  return getTextOrDefault(
    messages.requirementsNotMet,
    messages.passwordRequirementsNotMet,
    fieldName,
  );
};

const getUsernameRequirementsNotMetText = (fieldName) => {
  return getTextOrDefault(
    messages.requirementsNotMet,
    messages.usernameRequirementsNotMet,
    fieldName,
  );
};

const getUsernameIsRequiredText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldIsRequired,
    messages.usernameIsRequired,
    fieldName,
  );
};

const getCurrentPasswordIsRequiredText = (fieldName) => {
  return getTextOrDefault(
    messages.currentFieldIsRequired,
    messages.currentPasswordIsRequired,
    fieldName,
  );
};

const getUsernameRequirementsText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldRequirementLabel,
    messages.usernameRequirements,
    fieldName,
  );
};

const getPasswordIsRequiredText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldIsRequired,
    messages.passwordIsRequired,
    fieldName,
  );
};

const getNewPasswordIsRequiredText = (fieldName) => {
  return getTextOrDefault(
    messages.newFieldIsRequired,
    messages.newPasswordIsRequired,
    fieldName,
  );
};

const getConfirmPasswordIsRequiredText = (fieldName) => {
  return getTextOrDefault(
    messages.confirmFieldIsRequired,
    messages.confirmPasswordIsRequired,
    fieldName,
  );
};

const getConfirmNewPasswordIsRequiredText = (fieldName) => {
  return getTextOrDefault(
    messages.confirmNewFieldIsRequired,
    messages.confirmNewPasswordIsRequired,
    fieldName,
  );
};

const getPasswordRequirementsText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldRequirementLabel,
    messages.defaultFieldRequirementLabel,
    fieldName,
  );
};

const getPasswordsDoNotMatchText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldsDoNotMatch,
    messages.passwordsDoNotMatch,
    fieldName,
  );
};

const getNewPasswordsDoNotMatchText = (fieldName) => {
  return getTextOrDefault(
    messages.newFieldsDoNotMatch,
    messages.newPasswordsDoNotMatch,
    fieldName,
  );
};

const getInvalidNewPasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.invalidNewField,
    messages.invalidNewPassword,
    fieldName,
  );
};

const getInvalidConfirmPasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.invalidRepeatedField,
    messages.invalidRepeatedPassword,
    fieldName,
  );
};

const getPasswordUpdatedText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldUpdated,
    messages.passwordUpdated,
    fieldName,
  );
};

const getPasswordUpdatedSuccessfullyText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldUpdatedSuccessfully,
    messages.passwordUpdatedSuccessfully,
    fieldName,
  );
};

const getPasswordChangeUnavailableText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldChangeUnavailable,
    messages.passwordChangeUnavailable,
    fieldName,
  );
};

const getInvalidRecoveryKeyText = (parameters) => {
  const { fieldName, link } = parameters;

  return formatMessage(messages['page.resetpassword.invalid-recovery-key'], {
    fieldName: fieldName || formatMessage(messages.password),
    link: link,
  });
};

const getUsernameAlreadyInUseText = (fieldName) => {
  return getTextOrDefault(
    messages.fieldNameAlreadyInUse,
    messages.usernameAlreadyInUse,
    fieldName,
  );
};

const getInvalidCredentialsText = () => formatMessage(messages.AUTH_FAILURE);

const getNotAuthorizedToChangePasswordText = (fieldName) => {
  return getTextOrDefault(
    messages.notAuthorizedToChangeField,
    messages.notAuthorizedToChangePassword,
    fieldName,
  );
};

const getServerErrorHeading = () => {
  return formatMessage(messages.unableToProcessRequest);
};

const getServerErrorInstructionalText = (parameters) => {
  const { link } = parameters;

  return formatMessage(messages.unableToProcessRequestMessage, {
    link: link,
  });
};

const getClientErrorHeading = () => {
  return formatMessage(messages.invalidRequest);
};

const getClientErrorInstructionalText = (parameters) => {
  const { link } = parameters;

  return formatMessage(messages.unableToProcessClientRequestMessage, {
    link: link,
  });
};

const getAuthenticationCanceledHeader = (fieldName) => {
  return getTextOrDefault(
    messages.authenticationCanceledHeader,
    messages.defaultAuthenticationCanceledHeader,
    fieldName,
  );
};

const getAuthenticationCanceledMessage = (fieldName) => {
  return getTextOrDefault(
    messages.authenticationCanceledMessage,
    messages.defaultAuthenticationCanceledMessage,
    fieldName,
  );
};

const commonActions = {
  getText,
  getTextWithValues,
  getTextOrDefault,
  getUsernameText,
  getPasswordText,
  getCurrentPasswordText,
  getNewPasswordText,
  getConfirmPasswordText,
  getConfirmNewPasswordText,
  getPasswordRequirementsText,
  getUsernameRequirementsText,
  getRequiredFieldText,
  getLoginHelpText,
  getRequestPasswordText,
  getChangePasswordText,
  getPasswordRequirementsNotMetText,
  getUsernameRequirementsNotMetText,
  getUsernameIsRequiredText,
  getPasswordIsRequiredText,
  getCurrentPasswordIsRequiredText,
  getNewPasswordIsRequiredText,
  getConfirmPasswordIsRequiredText,
  getConfirmNewPasswordIsRequiredText,
  getPasswordsDoNotMatchText,
  getNewPasswordsDoNotMatchText,
  getInvalidNewPasswordText,
  getInvalidConfirmPasswordText,
  getPasswordUpdatedText,
  getPasswordUpdatedSuccessfullyText,
  getPasswordChangeUnavailableText,
  getInvalidRecoveryKeyText,
  getUsernameAlreadyInUseText,
  getInvalidCredentialsText,
  getNotAuthorizedToChangePasswordText,
  getServerErrorHeading,
  getServerErrorInstructionalText,
  getClientErrorHeading,
  getClientErrorInstructionalText,
  getAuthenticationCanceledHeader,
  getAuthenticationCanceledMessage,
};

export default commonActions;
